/* slider.js — KOSHO SG hero crossfade slider.
 * ----------------------------------------------------------------------------
 * Spec:
 *   - Crossfade only (no slide / no rotate).
 *   - Autoplay every 5s; pauses 10s after any user interaction; then resumes.
 *   - Disabled entirely when prefers-reduced-motion: reduce.
 *   - Disabled (and controls hidden) when there is only 1 slide.
 *   - Keyboard: ← / → step prev/next when slider has focus.
 *   - DOM contract:
 *       [data-slider]           — root
 *       [data-slide]            — each slide article
 *       [data-slider-prev]      — prev button
 *       [data-slider-next]      — next button
 *       [data-slider-dot]       — pagination dot (data-index="N")
 *       [data-slider-live]      — sr-only live region (text mirror of active)
 *   - ARIA: root carries aria-roledescription="carousel"; live region is
 *     aria-live="polite" and reads the active slide's data-slide-label.
 *   - No transform-based motion. Active slide flips aria-hidden="false",
 *     CSS owns the opacity crossfade.
 * ----------------------------------------------------------------------------
 * Exposes window.KoshoSlider = { mountAll(), Slider }.
 */

(function (window, document) {
  'use strict';

  var AUTOPLAY_INTERVAL = 5000;     // ms between auto advances
  var PAUSE_AFTER_INPUT = 10000;    // ms autoplay pause after user input
  var CROSSFADE_MS      = 300;      // matches --top-hero-crossfade

  var prefersReducedMotion = function () {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  };

  /* ------------------------------------------------------------------------
   * Slider constructor
   * ------------------------------------------------------------------------ */
  function Slider(root) {
    this.root      = root;
    this.slides    = Array.prototype.slice.call(root.querySelectorAll('[data-slide]'));
    this.prevBtn   = root.querySelector('[data-slider-prev]');
    this.nextBtn   = root.querySelector('[data-slider-next]');
    this.dots      = Array.prototype.slice.call(root.querySelectorAll('[data-slider-dot]'));
    this.live      = root.querySelector('[data-slider-live]');

    this.index            = 0;
    this.autoplayTimer    = 0;
    this.pauseTimer       = 0;
    this.rafId            = 0;
    this.userInteracting  = false;
    this.destroyed        = false;
    this.disabled         = this.slides.length < 2 || prefersReducedMotion();

    this._init();
  }

  Slider.prototype._init = function () {
    var self = this;

    // ARIA scaffolding.
    this.root.setAttribute('aria-roledescription', 'carousel');
    if (this.live) {
      this.live.setAttribute('aria-live', 'polite');
      this.live.setAttribute('aria-atomic', 'true');
    }

    // Initial visibility.
    this.slides.forEach(function (slide, i) {
      slide.setAttribute('aria-hidden', i === 0 ? 'false' : 'true');
      slide.setAttribute('role', 'group');
      slide.setAttribute('aria-roledescription', 'slide');
      slide.setAttribute('aria-label', (i + 1) + ' of ' + self.slides.length);
    });
    this._syncDots();
    this._announce();

    // Apply responsive background images (data-bg-pc / data-bg-mobile).
    this._applyResponsiveBackgrounds();

    if (this.disabled) {
      // Hide controls when there's nothing to cycle.
      this._hideControls();
      return;
    }

    // Wire controls.
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function () { self._userStep(-1); });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () { self._userStep(1); });
    }
    this.dots.forEach(function (dot) {
      dot.addEventListener('click', function () {
        var target = parseInt(dot.getAttribute('data-index'), 10);
        if (!isNaN(target)) self._userGoTo(target);
      });
    });

    // Keyboard.
    this.root.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowLeft')  { e.preventDefault(); self._userStep(-1); }
      if (e.key === 'ArrowRight') { e.preventDefault(); self._userStep(1); }
    });

    // Pause autoplay on hover / focus inside root.
    this.root.addEventListener('mouseenter', function () { self._pauseAutoplay(); });
    this.root.addEventListener('mouseleave', function () { self._scheduleAutoplay(AUTOPLAY_INTERVAL); });
    this.root.addEventListener('focusin',    function () { self._pauseAutoplay(); });
    this.root.addEventListener('focusout',   function () { self._scheduleAutoplay(AUTOPLAY_INTERVAL); });

    // Pause when tab not visible. Stored as a named reference on the instance
    // so destroy() can remove it — an anonymous listener would leak across
    // re-mounts (each mount adds another document-level handler that fires its
    // own _scheduleAutoplay, stacking autoplay timers).
    self._onVisibility = function () {
      if (document.hidden) self._pauseAutoplay();
      else                 self._scheduleAutoplay(AUTOPLAY_INTERVAL);
    };
    document.addEventListener('visibilitychange', self._onVisibility);

    this._scheduleAutoplay(AUTOPLAY_INTERVAL);
  };

  Slider.prototype._hideControls = function () {
    if (this.prevBtn) this.prevBtn.hidden = true;
    if (this.nextBtn) this.nextBtn.hidden = true;
    this.dots.forEach(function (d) { d.hidden = true; });
  };

  /* ------------------------------------------------------------------------
   * Responsive backgrounds: each [data-slide] (or descendant carrying the
   * attributes) may declare data-bg-pc / data-bg-mobile. The slider applies
   * the appropriate URL as a background-image via inline style based on
   * window.matchMedia('(min-width: 768px)') and re-syncs on breakpoint
   * change. Slides without these attributes are skipped (CSS owns layout).
   * ------------------------------------------------------------------------ */
  Slider.prototype._applyResponsiveBackgrounds = function () {
    if (!window.matchMedia) return;
    var self = this;
    var mql = window.matchMedia('(min-width: 768px)');

    var targets = [];
    this.slides.forEach(function (slide) {
      if (slide.hasAttribute('data-bg-pc') || slide.hasAttribute('data-bg-mobile')) {
        targets.push(slide);
      }
      var nested = slide.querySelectorAll('[data-bg-pc], [data-bg-mobile]');
      Array.prototype.forEach.call(nested, function (el) { targets.push(el); });
    });
    if (!targets.length) return;

    function apply() {
      var isPc = mql.matches;
      targets.forEach(function (el) {
        var url = isPc
          ? (el.getAttribute('data-bg-pc') || el.getAttribute('data-bg-mobile'))
          : (el.getAttribute('data-bg-mobile') || el.getAttribute('data-bg-pc'));
        if (url) {
          el.style.backgroundImage = 'url("' + url + '")';
          el.style.backgroundSize = 'cover';
          el.style.backgroundPosition = 'center';
          el.style.backgroundRepeat = 'no-repeat';
        }
      });
    }

    apply();

    // Listen for breakpoint flips. Modern API + legacy fallback.
    if (typeof mql.addEventListener === 'function') {
      mql.addEventListener('change', apply);
    } else if (typeof mql.addListener === 'function') {
      mql.addListener(apply);
    }
    self._bgMql = mql;
    self._bgApply = apply;
  };

  Slider.prototype._scheduleAutoplay = function (delay) {
    var self = this;
    if (this.destroyed || this.disabled || this.userInteracting) return;
    this._pauseAutoplay();
    this.autoplayTimer = window.setTimeout(function tick() {
      self._step(1);
      self.autoplayTimer = window.setTimeout(tick, AUTOPLAY_INTERVAL);
    }, delay);
  };

  Slider.prototype._pauseAutoplay = function () {
    if (this.autoplayTimer) {
      window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = 0;
    }
  };

  Slider.prototype._userStep = function (dir) {
    this._step(dir);
    this._registerUserInput();
  };

  Slider.prototype._userGoTo = function (target) {
    this._goTo(target);
    this._registerUserInput();
  };

  Slider.prototype._registerUserInput = function () {
    var self = this;
    this.userInteracting = true;
    this._pauseAutoplay();
    if (this.pauseTimer) window.clearTimeout(this.pauseTimer);
    this.pauseTimer = window.setTimeout(function () {
      self.userInteracting = false;
      self._scheduleAutoplay(AUTOPLAY_INTERVAL);
    }, PAUSE_AFTER_INPUT);
  };

  Slider.prototype._step = function (dir) {
    if (this.destroyed) return;
    var n = this.slides.length;
    if (n < 2) return;
    var next = (this.index + dir + n) % n;
    this._goTo(next);
  };

  Slider.prototype._goTo = function (target) {
    if (this.destroyed) return;
    var n = this.slides.length;
    if (n < 2) return;
    if (target < 0 || target >= n) return;
    if (target === this.index) return;

    var self = this;
    var from = this.index;
    this.index = target;

    // Use rAF so the opacity transitions are committed on the next paint.
    if (this.rafId) window.cancelAnimationFrame(this.rafId);
    this.rafId = window.requestAnimationFrame(function () {
      self.slides[from].setAttribute('aria-hidden', 'true');
      self.slides[target].setAttribute('aria-hidden', 'false');
      self._syncDots();
      self._announce();
    });
  };

  Slider.prototype._syncDots = function () {
    var self = this;
    this.dots.forEach(function (dot) {
      var i = parseInt(dot.getAttribute('data-index'), 10);
      var isActive = i === self.index;
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
      if (isActive) dot.setAttribute('tabindex', '0');
      else          dot.setAttribute('tabindex', '-1');
    });
  };

  Slider.prototype._announce = function () {
    if (!this.live) return;
    var slide = this.slides[this.index];
    if (!slide) return;
    var label = slide.getAttribute('data-slide-label') || slide.textContent.trim().slice(0, 120);
    this.live.textContent = 'Slide ' + (this.index + 1) + ' of ' + this.slides.length + ': ' + label;
  };

  Slider.prototype.destroy = function () {
    this._pauseAutoplay();
    if (this.pauseTimer) window.clearTimeout(this.pauseTimer);
    if (this.rafId)      window.cancelAnimationFrame(this.rafId);
    if (this._onVisibility) {
      document.removeEventListener('visibilitychange', this._onVisibility);
      this._onVisibility = null;
    }
    this.destroyed = true;
  };

  /* ------------------------------------------------------------------------
   * Auto-mount on DOMContentLoaded
   * ------------------------------------------------------------------------ */
  function mountAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-slider]');
    var instances = [];
    Array.prototype.forEach.call(roots, function (root) {
      if (root.__koshoSlider) return; // idempotent
      var s = new Slider(root);
      root.__koshoSlider = s;
      instances.push(s);
    });
    return instances;
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', mountAll);
  } else {
    mountAll();
  }

  window.KoshoSlider = { mountAll: mountAll, Slider: Slider, CROSSFADE_MS: CROSSFADE_MS };

})(window, document);
